import { Checkbox, TextInput } from '@kcftech/design-system';
import {
    getMaxValueRules,
    getMinValueRules,
    getNumericValueRules,
} from 'pages/Settings/AssemblyType/parameter_form/shared';
import { MinMaxInputContainer } from 'pages/Settings/AssemblyType/parameter_form/styled';
import {
    AssemblyParameterFormInputProps,
    typeGuardAssemblyParameterForm,
} from 'pages/Settings/AssemblyType/parameter_form/types';
import { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';

const MinMaxInclusiveContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-top: 1rem;
`;

export function AssemblyParameterDecimalFormContent({
    form: _form,
}: AssemblyParameterFormInputProps) {
    const form = typeGuardAssemblyParameterForm(_form, 'decimal');

    const {
        control,
        register,
        trigger,
        formState: { errors },
        watch,
    } = form;

    const { type, minValue, maxValue, minValueInclusive, maxValueInclusive } = watch();

    const minValueRules = useMemo(
        () => getMinValueRules(type, maxValue),
        [maxValue, type],
    );

    const maxValueRules = useMemo(
        () => getMaxValueRules(type, minValue),
        [minValue, type],
    );

    const defaultValueRules = useMemo(
        () =>
            getNumericValueRules({
                type,
                fieldName: 'Default Value',
                minValue,
                maxValue,
                minValueInclusive,
                maxValueInclusive,
            }),
        [maxValue, maxValueInclusive, minValue, minValueInclusive, type],
    );

    useEffect(() => {
        trigger('defaultValue');
    }, [minValue, maxValue, minValueInclusive, maxValueInclusive, trigger]);

    return (
        <>
            <div>
                <MinMaxInputContainer>
                    <TextInput
                        label="Min Value"
                        placeholder="Enter a minimum value"
                        error={errors.minValue?.message}
                        width="26rem"
                        {...register('minValue', {
                            ...minValueRules,
                            deps: ['maxValue'],
                        })}
                    />

                    <TextInput
                        label="Max Value"
                        placeholder="Enter a maximum value"
                        error={errors.maxValue?.message}
                        width="26rem"
                        {...register('maxValue', {
                            ...maxValueRules,
                            deps: ['minValue'],
                        })}
                    />
                </MinMaxInputContainer>

                <MinMaxInclusiveContainer>
                    <Controller
                        control={control}
                        name="minValueInclusive"
                        render={({ field: { value, onChange } }) => (
                            <Checkbox
                                label="Min Inclusive"
                                checked={value ?? false}
                                onChange={onChange}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="maxValueInclusive"
                        render={({ field: { value, onChange } }) => (
                            <Checkbox
                                label="Max Inclusive"
                                checked={value ?? false}
                                onChange={onChange}
                            />
                        )}
                    />
                </MinMaxInclusiveContainer>
            </div>
            <TextInput
                label="Default Value"
                placeholder="Enter a default value"
                error={errors.defaultValue?.message}
                {...register('defaultValue', defaultValueRules)}
            />
        </>
    );
}
